/**
 * An app's Home tab for me (Slack's App Home; contracts apps.ts AppHome): the view the app published with
 * `views.publish`, shown in the app's DM above Messages, and what I enter in it.
 *
 * - **Opening** loads the tab (`appHome`), which also tells the app (`app_home_opened`, Events API) so it can publish
 *   a fresh view; the view then arrives as the `app.home` realtime event (`apply`).
 * - **Values** start from `initialKitValues`; when the app republishes, values of elements still there are kept.
 * - **Elements:** buttons, overflow menus, selects and pickers (and dispatching inputs) send `block_actions` with
 *   `container: { type: "view" }` at once (`homeAction`); the app answers with `views.publish`.
 *
 * One per app (WorkspaceViewModel.appHome). Clients without the API show no tab content.
 */
import type { AppHome, KitStateValues } from "@bundle/contracts";
import { type KitButtonNode, type KitNode, type KitOverflowNode, type KitValues, initialKitValues, kitState, kitStateValue, kitValueElements, renderKit } from "../logic/blockKit.ts";
import { type MessageDescriptor, msg } from "../i18n.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type AppHomeState = {
  status: "idle" | "loading" | "ready" | "error";
  home?: AppHome;
  /** The view drawn (renderKit of its blocks); empty before the app publishes one. */
  nodes: KitNode[];
  values: KitValues;
  notice?: MessageDescriptor;
};

export class AppHomeViewModel extends ViewModel<AppHomeState> {
  private readonly ws: WorkspaceViewModel;
  readonly appId: string;

  constructor(ws: WorkspaceViewModel, appId: string) {
    super({ status: "idle", nodes: [], values: {} });
    this.ws = ws;
    this.appId = appId;
  }

  /** Opens the tab: loads the published view and tells the app (it may publish a new one right after). */
  open(): void {
    const get = this.ws.deps.api.appHome;
    if (get === undefined) return;
    if (this.state.home === undefined) this.update({ status: "loading" });
    get.call(this.ws.deps.api, this.ws.wsId, this.appId).then(
      (home) => this.apply(home),
      () => {
        if (!this.isDisposed && this.state.home === undefined) this.update({ status: "error" });
      },
    );
  }

  /** A view from the server (the open answer or the `app.home` event). Values of elements still there are kept. */
  apply(home: AppHome): void {
    if (home.appId !== this.appId) return;
    const before = this.state.home;
    if (before !== undefined && before.viewId === home.viewId && before.hash === home.hash && this.state.status === "ready") {
      this.update({ home });
      return;
    }
    const nodes = home.view === undefined ? [] : renderKit(home.view.blocks);
    const values = initialKitValues(nodes);
    for (const { blockId, element } of kitValueElements(nodes)) {
      const v = this.state.values[blockId]?.[element.actionId];
      if (v !== undefined && before?.viewId === home.viewId) (values[blockId] ??= {})[element.actionId] = v;
    }
    const next: AppHomeState = { status: "ready", home, nodes, values };
    this.setState(next);
  }

  private find(blockId: string, actionId: string) {
    for (const n of this.state.nodes) {
      if (n.kind === "input" && n.blockId === blockId && n.element.actionId === actionId) return { element: n.element, input: true, dispatch: n.dispatch };
    }
    const found = kitValueElements(this.state.nodes).find((x) => x.blockId === blockId && x.element.actionId === actionId);
    return found === undefined ? undefined : { element: found.element, input: found.input, dispatch: false };
  }

  /** An element's new value; elements outside inputs (and dispatching selects) tell the app at once. */
  setValue(blockId: string, actionId: string, value: string[]): void {
    const found = this.find(blockId, actionId);
    if (found === undefined) return;
    this.update({ values: { ...this.state.values, [blockId]: { ...this.state.values[blockId], [actionId]: value } } });
    if ((!found.input || found.dispatch) && found.element.kind !== "text_input") this.send(blockId, actionId, undefined, kitStateValue(found.element, value, this.ws.kitLabels[blockId]?.[actionId]));
  }

  /** Enter in a dispatching text input. */
  dispatchText(blockId: string, actionId: string): void {
    const found = this.find(blockId, actionId);
    if (found === undefined || !found.dispatch) return;
    this.send(blockId, actionId, undefined, kitStateValue(found.element, this.state.values[blockId]?.[actionId] ?? [], this.ws.kitLabels[blockId]?.[actionId]));
  }

  press(blockId: string, button: KitButtonNode, openUrl?: (url: string) => void): void {
    if (button.url !== undefined) openUrl?.(button.url);
    this.send(blockId, button.actionId, button.value, undefined);
  }

  pickOverflow(blockId: string, menu: KitOverflowNode, value: string): void {
    const o = menu.options.find((x) => x.value === value);
    if (o === undefined) return;
    this.send(blockId, menu.actionId, undefined, { type: "overflow", selected_option: { text: { type: "plain_text", text: o.label }, value } });
  }

  clearNotice(): void {
    if (this.state.notice === undefined) return;
    const { notice: _n, ...rest } = this.state;
    this.setState(rest);
  }

  private send(blockId: string, actionId: string, value: string | undefined, selected: ReturnType<typeof kitStateValue> | undefined): void {
    const act = this.ws.deps.api.homeAction;
    if (act === undefined || this.state.home?.view === undefined) return;
    const req: { actionId: string; blockId: string; values: KitStateValues; value?: string; selected?: ReturnType<typeof kitStateValue> } = { actionId, blockId, values: kitState(this.state.nodes, this.state.values, this.ws.kitLabels) };
    if (value !== undefined) req.value = value;
    if (selected !== undefined) req.selected = selected;
    act.call(this.ws.deps.api, this.ws.wsId, this.appId, req).then(
      () => undefined,
      () => {
        if (!this.isDisposed) this.update({ notice: msg("composer.appAction.failed") });
      },
    );
  }
}
